'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Check, FileText, Hash, List, Code, FileInput } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { lineCounterContent } from '../../data/line-counter-content';
import Link from 'next/link';

type CountResult = {
    lines: number;
    nonEmptyLines: number;
    words: number;
    characters: number;
    bytes: number;
};

export default function LineCounter() {
    const [text, setText] = useState('');
    const [countResult, setCountResult] = useState<CountResult>({
        lines: 0,
        nonEmptyLines: 0,
        words: 0,
        characters: 0,
        bytes: 0
    });
    const [copied, setCopied] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const { toast } = useToast();

    const calculateCounts = useCallback((text: string) => {
        const lines = text === '' ? 0 : text.split('\n').length;
        const nonEmptyLines = text === '' ? 0 : text.split('\n').filter(line => line.trim() !== '').length;
        const words = text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
        const characters = text.length;
        const bytes = new TextEncoder().encode(text).length;

        return {
            lines,
            nonEmptyLines,
            words,
            characters,
            bytes
        };
    }, []);

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = e.target.value;
        setText(newText);
        setCountResult(calculateCounts(newText));
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const fileContent = event.target?.result as string;
            setText(fileContent);
            setCountResult(calculateCounts(fileContent));
        };
        reader.readAsText(file);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    };

    const clearText = () => {
        setText('');
        setCountResult({
            lines: 0,
            nonEmptyLines: 0,
            words: 0,
            characters: 0,
            bytes: 0
        });
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Line Counter">
                Count lines in text, code, or documents with our free online line counter. Perfect for developers, writers, and content analysis.
            </ToolPageIntro>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <div>
                                    <CardTitle>Text Input</CardTitle>
                                    <CardDescription>Type, paste, or upload a file to count lines</CardDescription>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        className="text-xs"
                                    >
                                        {showAdvanced ? 'Hide Details' : 'Show Details'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="relative">
                                <Textarea
                                    value={text}
                                    onChange={handleTextChange}
                                    placeholder="Type or paste your text here..."
                                    className="min-h-[300px] w-full font-mono text-sm"
                                    rows={10}
                                />
                                <div className="absolute right-3 top-3 flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={copyToClipboard}
                                        disabled={!text}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Copy to clipboard"
                                    >
                                        {copied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearText}
                                        disabled={!text}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Clear text"
                                    >
                                        <RefreshCw className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="relative">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        asChild
                                        className="cursor-pointer"
                                    >
                                        <Label htmlFor="file-upload" className="cursor-pointer">
                                            <FileInput className="h-4 w-4 mr-2" />
                                            Upload File
                                            <input
                                                id="file-upload"
                                                type="file"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                accept=".txt,.js,.jsx,.ts,.tsx,.html,.css,.json,.md,.py,.java,.c,.cpp,.cs,.php,.rb,.go,.rs"
                                            />
                                        </Label>
                                    </Button>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    Supports: .txt, .js, .ts, .html, .css, .json, .md, .py, .java, .c, .cpp, .cs, .php, .rb, .go, .rs
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Line Count</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="text-center">
                                <div className="text-5xl font-bold">{countResult.lines.toLocaleString()}</div>
                                <div className="text-muted-foreground">Total Lines</div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">Non-empty lines:</span>
                                    <span className="font-medium">{countResult.nonEmptyLines.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">Empty lines:</span>
                                    <span className="font-medium">{(countResult.lines - countResult.nonEmptyLines).toLocaleString()}</span>
                                </div>

                                {showAdvanced && (
                                    <>
                                        <div className="border-t pt-4 mt-4">
                                            <h3 className="font-medium mb-2">Advanced Metrics</h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">Words:</span>
                                                    <span className="font-medium">{countResult.words.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">Characters:</span>
                                                    <span className="font-medium">{countResult.characters.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">Bytes:</span>
                                                    <span className="font-medium">{countResult.bytes.toLocaleString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>About Line Counter</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 text-sm text-muted-foreground">
                            <p>
                                This tool helps you count lines, words, and characters in your text or code files.
                            </p>
                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground">How it works:</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Lines: Counts all lines, including empty ones</li>
                                    <li>Non-empty lines: Counts only lines with content</li>
                                    <li>Words: Counts sequences of non-whitespace characters</li>
                                    <li>Characters: Counts all characters including spaces</li>
                                    <li>Bytes: Size of the text in bytes (UTF-8 encoding)</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={lineCounterContent.title}
                    description={lineCounterContent.description}
                    content={lineCounterContent.content}
                    faqs={lineCounterContent.faqs}
                    relatedTools={lineCounterContent.relatedTools}
                />
            </div>
        </div>
    );
}
